/**
 * Club Customization Form Validation
 * Handles all validation logic for the club customization forms
 */

document.addEventListener('DOMContentLoaded', function() {
    /**
     * Switch to a specific tab
     * @param {string} targetTab - The tab name to switch to
     */
    function switchToTab(targetTab) {
        const tabButtons = document.querySelectorAll('.tab-button');
        const tabPanels = document.querySelectorAll('.tab-panel');

        tabButtons.forEach(btn => btn.classList.remove('active'));
        tabPanels.forEach(panel => panel.classList.remove('active'));

        const targetButton = document.querySelector(`.tab-button[data-tab="${targetTab}"]`);
        const targetPanel = document.querySelector(`[data-panel="${targetTab}"]`);

        if (targetButton && targetPanel) {
            targetButton.classList.add('active');
            targetPanel.classList.add('active');
        } else {
            console.warn('Could not find tab:', targetTab, 'Button:', targetButton, 'Panel:', targetPanel);
        }
    }

    /**
     * Check if the custom form is valid (all required fields filled)
     * Updates payment button visibility based on validation state
     */
    function updatePaymentButtonsVisibility() {
        const customForm = document.querySelector('.club-customization-form');
        const paymentButtons = document.querySelectorAll('.payment-buttons');

        if (!customForm || customForm.style.display === 'none') {
            // If custom form is hidden (prebuilt form is active), show payment buttons
            paymentButtons.forEach(btn => btn.style.display = 'block');
            return;
        }

        // Check if form is valid
        const isValid = validateFormTabs(true); // true = silent validation (no UI feedback)

        // Show or hide payment buttons based on validation
        paymentButtons.forEach(btn => {
            btn.style.display = isValid ? 'block' : 'none';
        });
    }

    /**
     * Validate all tabs and switch to first tab with errors
     * Returns true if all required fields are valid
     * @param {boolean} silent - If true, don't show UI feedback or switch tabs
     */
    function validateFormTabs(silent = false) {
        const customForm = document.querySelector('.club-customization-form');
        if (!customForm) return true;

        const panels = Array.from(document.querySelectorAll('.tab-panel'));

        // Find first tab with invalid required fields
        let firstInvalidTab = null;
        let firstInvalidFormField = null;

        for (let i = 0; i < panels.length; i++) {
            const panel = panels[i];
            const tabName = panel.getAttribute('data-panel');

            // Get all required form-fields in this tab
            const requiredFormFields = panel.querySelectorAll('.form-field.required');

            for (const formField of requiredFormFields) {
                let isValid = false;

                // Check what type of input is inside the form-field
                const checkbox = formField.querySelector('input[type="checkbox"]');
                const radioButtons = formField.querySelectorAll('input[type="radio"]');
                const select = formField.querySelector('select');
                const textInput = formField.querySelector('input[type="text"], input[type="hidden"], textarea');

                if (checkbox) {
                    // Checkbox validation
                    isValid = checkbox.checked;
                } else if (radioButtons.length > 0) {
                    // Radio button validation - check if any in the group is checked
                    isValid = Array.from(radioButtons).some(radio => radio.checked);
                } else if (select) {
                    // Select validation
                    isValid = select.value !== '' && select.value !== null;
                } else if (textInput) {
                    // Text input validation
                    if (textInput.type === 'hidden' && textInput.id === 'shaft-model-value') {
                        // Special handling for hidden shaft model input
                        isValid = textInput.value !== '' && textInput.value !== null;
                    } else {
                        isValid = textInput.value.trim() !== '';
                    }
                }

                if (!isValid && !firstInvalidTab) {
                    firstInvalidTab = tabName;
                    firstInvalidFormField = formField;
                    break;
                }
            }

            if (firstInvalidTab) break;
        }

        // If silent mode, just return validation result
        if (silent) {
            return !firstInvalidTab;
        }

        // If there are invalid fields, switch to that tab and highlight the field
        if (firstInvalidTab) {
            console.log('Switching to tab with invalid field:', firstInvalidTab);
            switchToTab(firstInvalidTab);

            // Highlight the first invalid field
            if (firstInvalidFormField) {
                // Wait for tab transition to complete before scrolling
                setTimeout(() => {
                    // Verify the field is visible before scrolling
                    const isVisible = firstInvalidFormField.offsetParent !== null;
                    console.log('Invalid field visible:', isVisible, firstInvalidFormField);

                    if (isVisible) {
                        firstInvalidFormField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }

                    // Add visual feedback
                    firstInvalidFormField.classList.add('field-error');
                    setTimeout(() => firstInvalidFormField.classList.remove('field-error'), 3000);

                    // For the shaft model button, also add error class to the button
                    const hiddenInput = firstInvalidFormField.querySelector('#shaft-model-value');
                    if (hiddenInput) {
                        const selectButton = firstInvalidFormField.querySelector('.shaft-model-select-button');
                        if (selectButton) {
                            selectButton.classList.add('field-error');
                            setTimeout(() => selectButton.classList.remove('field-error'), 3000);
                        }
                    }

                    // Try to focus the first focusable element in the form field
                    const focusable = firstInvalidFormField.querySelector('input:not([type="hidden"]), select, textarea, button');
                    if (focusable) {
                        focusable.focus();
                    }
                }, 350);
            }


            return false;
        }

        return true;
    }

    /**
     * Remove hidden form fields from FormData before submission
     * This ensures that disabled/hidden inputs from inactive forms are not submitted
     */
    function cleanHiddenFormFields() {
        const prebuiltForm = document.querySelector('.prebuilt-form');
        const customForm = document.querySelector('.club-customization-form');

        if (!prebuiltForm || !customForm) return;

        // Determine which form is currently hidden
        const isPrebuiltHidden = prebuiltForm.style.display === 'none';
        const isCustomHidden = customForm.style.display === 'none';

        // Get all inputs from the hidden form
        const hiddenFormContainer = isPrebuiltHidden ? prebuiltForm : (isCustomHidden ? customForm : null);

        if (hiddenFormContainer) {
            // Get all form inputs (including hidden, select, textarea, etc.)
            const hiddenInputs = hiddenFormContainer.querySelectorAll('input, select, textarea');

            hiddenInputs.forEach(input => {
                // Store the original name so we can restore it if needed
                if (!input.hasAttribute('data-original-name')) {
                    input.setAttribute('data-original-name', input.name);
                }
                // Clear the name attribute so it won't be submitted
                input.name = '';
            });
        }

        // Ensure inputs in the visible form have their names
        const visibleFormContainer = isPrebuiltHidden ? customForm : (isCustomHidden ? prebuiltForm : null);

        if (visibleFormContainer) {
            const visibleInputs = visibleFormContainer.querySelectorAll('input, select, textarea');

            visibleInputs.forEach(input => {
                // Restore original name if it was cleared
                const originalName = input.getAttribute('data-original-name');
                if (originalName && !input.name) {
                    input.name = originalName;
                }
            });
        }
    }

    /**
     * Intercept form submission and validate tabs
     */
    function initFormValidation() {
        const productForm = document.querySelector('form[action*="/cart/add"]');

        if (!productForm) return;

        // Disable HTML5 validation to prevent "not focusable" errors on hidden fields
        productForm.setAttribute('novalidate', 'novalidate');

        // Validation handler for both regular form submission and payment button
        function handleValidation(e) {
            const customForm = document.querySelector('.club-customization-form');

            // Clean hidden form fields before validation
            cleanHiddenFormFields();

            // Only validate if custom form is visible
            if (customForm && customForm.style.display !== 'none') {
                if (!validateFormTabs()) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    // Show error message
                    const errorMessage = document.createElement('div');
                    errorMessage.className = 'validation-error-message';
                    errorMessage.textContent = 'Please fill in all required fields before adding to cart.';

                    // Remove any existing error messages
                    const existingError = document.querySelector('.validation-error-message');
                    if (existingError) {
                        existingError.remove();
                    }

                    // Insert error message at the top of the form
                    customForm.insertBefore(errorMessage, customForm.firstChild);

                    // Remove error message after 5 seconds
                    setTimeout(() => errorMessage.remove(), 5000);

                    return false;
                }
            }
        }

        // Handle regular form submission
        productForm.addEventListener('submit', handleValidation, true);
    }

    /**
     * Initialize real-time validation for payment buttons visibility
     */
    function initPaymentButtonsControl() {
        const customForm = document.querySelector('.club-customization-form');

        if (!customForm) return;

        // Initial check
        updatePaymentButtonsVisibility();

        // Listen for form changes to update payment button visibility
        customForm.addEventListener('change', updatePaymentButtonsVisibility);
        customForm.addEventListener('input', updatePaymentButtonsVisibility);
    }

    // Initialize form validation
    initFormValidation();

    // Initialize payment buttons control after form is ready
    initPaymentButtonsControl();

    // Make functions globally accessible so they can be called from club-customization.js
    window.updatePaymentButtonsVisibility = updatePaymentButtonsVisibility;
    window.cleanHiddenFormFields = cleanHiddenFormFields;
});
